import React from 'react';
import PropTypes from 'prop-types';
import {
  Navbar,
  NavItem,
  Button,
  Container,
  NavLink,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import {
  Menu,
  Grid,
  ChevronDown,
  Plus,
  HelpCircle,
  MessageCircle,
  Edit3,
  Users,
  FileText,
  Monitor,
  Briefcase,
  MessageSquare,
} from 'react-feather';
import { useSelector } from 'react-redux';
import axiosService from '@Lib/axiosService';
import { useHistory } from 'react-router-dom';
import SelectProjectModal from '@Components/SelectProjectModal';
import { toastError } from '@Utils/toastrHelper';
import UserDropdown from './UserDropdown';
import NotificationDropDown from './NotificationDropDown';
import ProjectsDropdown from './ProjectsDropdown';
import HeaderSearch from './HeaderSearch';
import { getCurrentSiteBranding } from '@Lib/siteBranding';
import AppSwitcher from '@Components/AppSwitcher';

function Header({ setMenuVisibility, windowWidth }) {

  const userInfo = useSelector((state) => state.user.info);
  const projectActive = useSelector((state) => state.user.projectActive);
  const accessPermisions = useSelector((state) => state.user.accessPermisions) || {};
  const siteBranding = getCurrentSiteBranding();

  const canCreateCustomer =
    accessPermisions.create_customers !== false && accessPermisions.customers;
  const canCreateOrder = accessPermisions.create_orders;
  const canCreateStockDocument = accessPermisions.warehouse_stock_document_create;
  const canCreateShippingReceipt = accessPermisions.reports_shipping_receipts;

  const isSale = React.useMemo(() => {
    if (!userInfo) return false;
    if (userInfo.is_super_admin) return true;
    const projectRoles = userInfo.roles_by_project?.[projectActive?.id] || [];
    return projectRoles.some((role) => role.toLowerCase().includes('sale'));
  }, [userInfo, projectActive]);

  const handleOpenZaloOmni = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosService.get(`${process.env.URL}/sso/zalo-omni-token`);
      const { redirect_url } = response.data;
      window.open(redirect_url, '_blank');
    } catch (error) {
      console.error('Không thể mở Zalo Omni:', error);
      toastError('Không thể kết nối Zalo Omni. Vui lòng thử lại.');
    }
  };

  const getAppUrl = (baseUrl, redirectPath = '/dashboard') => {
    if (!baseUrl) return '#';
    const token = localStorage.getItem(process.env.TOKEN);
    if (token) {
      return `${baseUrl}/auth/callback?token=${token}&redirect=${redirectPath}`;
    }
    return baseUrl;
  };

  const history = useHistory();
  const [isOpenModal, setIsOpenModal] = React.useState(false);
  const [createType, setCreateType] = React.useState(null);

  const handleCreateNew = (type) => {
    if (type === 'stock_document') {
      history.push('/stock-management/documents/create');
      return;
    }

    if (type === 'shipping_receipt') {
      history.push('/reports/shippingReceipts/addNew');
      return;
    }

    setCreateType(type);
    setIsOpenModal(true);
  };

  const handleSelectProject = (projectId) => {
    setIsOpenModal(false);
    if (!projectId) return;
    if (createType === 'customer') {
      history.push(`/customer/addNew/${projectId}`);
    } else if (createType === 'order') {
      history.push(`/order/addNew/${projectId}`);
    }
  };

  return (
    <Navbar
      className={`header-navbar navbar-fixed align-items-center navbar-brand-center bg-white navbar-light navbar-expand ${windowWidth < 768 ? 'fixed-top' : ''
        }`}
    >
      <div
        className="navbar-container d-flex content align-items-center justify-content-between w-100"
        style={{ padding: '0 1rem' }}
      >
        <NavLink className="d-block d-md-none" onClick={setMenuVisibility}>
          <Menu />
        </NavLink>
        <div className="d-block d-md-none ml-50">
          <img
            src={siteBranding.mobile_logo_url || siteBranding.app_icon_url || siteBranding.logo_url}
            alt={siteBranding.short_name}
            style={{
              width: '32px',
              height: '32px',
              maxHeight: siteBranding.logo_max_height || undefined,
              borderRadius: '8px',
              objectFit: 'cover',
            }}
          />
        </div>

        <div className="d-flex align-items-center">
          <AppSwitcher mode="dropdown" />

          <div className="brand-logo d-md-block d-none mr-4">

            <img
              src={siteBranding.logo_url}
              className="site-logo"
              alt={siteBranding.name}
              style={{
                height: siteBranding.logo_max_height || '50px',
                maxHeight: siteBranding.logo_max_height || undefined,
                width: 'auto',
              }}
            />
          </div>
        </div>

        <div className="flex-grow-1 mx-4" style={{ maxWidth: '440px' }}>
          <HeaderSearch />
        </div>

        <div className="d-flex align-items-center ml-auto">
          {(canCreateCustomer ||
            canCreateOrder ||
            canCreateStockDocument ||
            canCreateShippingReceipt) && (
              <UncontrolledDropdown className="d-none d-md-block">
                <DropdownToggle
                  tag="button"
                  className="btn d-flex align-items-center rounded-pill font-weight-bold"
                  style={{
                    backgroundColor: '#fba92c',
                    border: 'none',
                    color: '#fff',
                    padding: '9px 20px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Plus size={16} className="mr-50" />
                  <div
                    className="d-flex flex-column align-items-center justify-content-center mx-50"
                    style={{ lineHeight: '1.2' }}
                  >
                    <span>Tạo mới</span>
                  </div>
                  <ChevronDown size={14} className="ml-50" />
                </DropdownToggle>
                <DropdownMenu right className="dropdown-menu-custom">
                  {canCreateCustomer && (
                    <DropdownItem onClick={() => handleCreateNew('customer')}>
                      <div className="menu-icon-wrapper">
                        <Users size={18} />
                      </div>
                      <span className="menu-title">Khách hàng</span>
                    </DropdownItem>
                  )}
                  {canCreateOrder && (
                    <DropdownItem onClick={() => handleCreateNew('order')}>
                      <div className="menu-icon-wrapper">
                        <FileText size={18} />
                      </div>
                      <span className="menu-title">Đơn hàng</span>
                    </DropdownItem>
                  )}
                  {canCreateStockDocument && (
                    <DropdownItem onClick={() => handleCreateNew('stock_document')}>
                      <div className="menu-icon-wrapper">
                        <Edit3 size={18} />
                      </div>
                      <span className="menu-title">Phiếu kho mới</span>
                    </DropdownItem>
                  )}
                  {canCreateShippingReceipt && (
                    <DropdownItem onClick={() => handleCreateNew('shipping_receipt')}>
                      <div className="menu-icon-wrapper">
                        <FileText size={18} />
                      </div>
                      <span className="menu-title">Biên bản bàn giao</span>
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            )}

          <div className="d-flex align-items-center">
            <NotificationDropDown />
            <div className="position-relative cursor-pointer mx-1">
              <MessageCircle size={22} color="#171725" strokeWidth={1.5} />
              <span
                className="position-absolute rounded-circle bg-success"
                style={{
                  width: '10px',
                  height: '10px',
                  top: '-1px',
                  right: '-1px',
                  border: '2px solid white',
                }}
              />
            </div>
          </div>
          <div
            style={{
              height: '28px',
              width: '1px',
              backgroundColor: '#e2e2ea',
              margin: '0 8px 0 0',
            }}
          />
          <UserDropdown />
        </div>
      </div>
      <SelectProjectModal
        isOpen={isOpenModal}
        toggle={() => setIsOpenModal(!isOpenModal)}
        onSubmit={handleSelectProject}
      />
    </Navbar>
  );
}

Header.propTypes = {
  user: PropTypes.object,
  setMenuVisibility: PropTypes.func,
  windowWidth: PropTypes.number,
};

export default Header;
